import React, { useCallback, useEffect, useRef } from 'react';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showWarning, ShowSuccessFetch } from 'src/utils/toast/toastUtil';
import { formatDate } from 'src/utils/string/stringUtil';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { gridRefSelectedRowHandler } from 'src/utils/grid/GridHandlerContent';
import {
	getWMS1120_R01Sales,
	getWMS1120_R01SalesAllCount,
	getWMS1120_R01SalesLot,
	getWMS1120_R01ProductBase,
	getWMS1120_R01ProductLotDetail,
	getWMS1120_R01ProductResult,
	getWMS1120_R01MaterialInput,
	getWMS1120_R01LotInbound,
} from "./Api_WMS1120_R01";
import {
	WMS1120_R01SalesDto,
	WMS1120_R01SalesLotDto,
	WMS1120_R01ProductBaseDto,
	WMS1120_R01ProductLotDto,
	WMS1120_R01ProductResultDto,
	WMS1120_R01BomDto,
} from './Dto_WMS1120_R01';
import { useWMS1120_R01Store } from './Store_WMS1120_R01';

interface Props {
	salesGridRef: React.RefObject<AgGridReact<WMS1120_R01SalesDto> | null>;
	productGridRef: React.RefObject<AgGridReact<WMS1120_R01ProductBaseDto> | null>;
	setMessage: (message: string) => void;
}

export const Content_WMS1120_R01 = (props: Props) => {
	const {
		salesGridRef,
		productGridRef,
		setMessage,
	} = props;

	const {
		WMS1120_R01activeTab: activeTab,
		WMS1120_R01Filter,
		WMS1120_R01dataList: dataList,
		setWMS1120_R01SalesList: setSalesList,
		setSelectedWMS1120_R01Sales: setSelectedSales,
		setWMS1120_R01SalesLotList: setSalesLotList,
		setSelectedWMS1120_R01SalesLot: setSelectedSalesLot,
		setWMS1120_R01SalesAllCount: setSalesAllCount,
		WMS1120_R01salesAllCount: salesAllCount,
		WMS1120_R01productBaseList: productBaseList,
		setWMS1120_R01ProductBaseList: setProductBaseList,
		setSelectedWMS1120_R01ProductBase: setSelectedProductBase,
		setWMS1120_R01ProductLotList: setProductLotList,
		setSelectedWMS1120_R01ProductLot: setSelectedProductLot,
		setWMS1120_R01ProductResultList: setProductResultList,
		setSelectedWMS1120_R01ProductResult: setSelectedProductResult,
		setWMS1120_R01BomList: setBomList,
		setSelectedWMS1120_R01Bom: setSelectedBom,
		setWMS1120_R01StockList: setStockList,
		resetAllWMS1120ScrollTop,
		resetWMS1120ScrollTop,
	} = useWMS1120_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	
	// 권한 정보 로드
	useFetchActionbarAuth("WMS1120_R01");

	// 매출기준 조회
	const fetchSalesData = useCallback(async () => {
		setLoadingSpinner(true);
		resetAllWMS1120ScrollTop();
		try {
			const params = {
				startDate: WMS1120_R01Filter.salesDateRange?.from ? formatDate(WMS1120_R01Filter.salesDateRange.from) : undefined,
				endDate: WMS1120_R01Filter.salesDateRange?.to ? formatDate(WMS1120_R01Filter.salesDateRange.to) : undefined,
				compCdCondition: WMS1120_R01Filter.salesCompCdCondition || undefined,
				customCdCondition: WMS1120_R01Filter.salesCustomCdCondition || undefined,
				listLength: dataList.length || 0,
			};

		const response = await getWMS1120_R01Sales('search', params);
		const newList = response.data.data || [];
		setSalesList(newList);

		const countResponse = await getWMS1120_R01SalesAllCount(params);
		const totalCount = countResponse.data.data || 0;
		setSalesAllCount(totalCount);

		if (newList.length > 0) {
			// 첫 번째 행 자동 선택 (UI)
			gridRefSelectedRowHandler(
				salesGridRef,
				setSelectedSales,
				() => true,
			);

			const firstRow = newList[0];
			// 첫 행 기준 연쇄 조회: Lot → 생산정보 → BOM → 입고이력
			const lotRes = await getWMS1120_R01SalesLot(firstRow.salesBillNo);
			const lotList: WMS1120_R01SalesLotDto[] = lotRes.data.data || [];
			setSalesLotList(lotList);
			// 선택 행 하이라이트용 valueIndex 부여 (Store와 동일하게 1부터)
			setSelectedSalesLot(lotList.length > 0 ? { ...lotList[0], valueIndex: 1 } : undefined);

			if (lotList.length > 0) {
				const prRes = await getWMS1120_R01ProductResult(lotList[0].lotNo);
				const productResultList: WMS1120_R01ProductResultDto[] = prRes.data.data || [];
				setProductResultList(productResultList);
				setSelectedProductResult(productResultList.length > 0 ? productResultList[0] : undefined);

				if (productResultList.length > 0) {
					const bomRes = await getWMS1120_R01MaterialInput(productResultList[0].productResultNo);
					const bomListData: WMS1120_R01BomDto[] = bomRes.data.data || [];
					setBomList(bomListData);
					setSelectedBom(bomListData.length > 0 ? { ...bomListData[0], valueIndex: 1 } : undefined);

					if (bomListData.length > 0) {
						const stockRes = await getWMS1120_R01LotInbound(bomListData[0].lotNo);
						setStockList(stockRes.data.data || []);
					} else {
						setStockList([]);
					}
				} else {
					setBomList([]);
					setSelectedBom(undefined);
					setStockList([]);
				}
			} else {
				setProductResultList([]);
				setSelectedProductResult(undefined);
				setBomList([]);
				setSelectedBom(undefined);
				setStockList([]);
			}

			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${totalCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			setSelectedSales(undefined);
			setSalesLotList([]);
			setSelectedSalesLot(undefined);
			setProductResultList([]);
			setSelectedProductResult(undefined);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
		}
		} catch (error) {
			// console.error('매출기준 조회 오류:', error);
			showWarning('조회 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [WMS1120_R01Filter, dataList.length, setSalesList, setSelectedSales, setSalesLotList, setSelectedSalesLot, setSalesAllCount, setProductResultList, setSelectedProductResult, setBomList, setSelectedBom, setStockList, setMessage, setLoadingSpinner, salesGridRef, resetAllWMS1120ScrollTop]);

	// 생산기준 조회
	const fetchProductData = useCallback(async () => {
		setLoadingSpinner(true);
		resetAllWMS1120ScrollTop();
		try {
			const params = {
				startDate: WMS1120_R01Filter.productDateRange?.from ? formatDate(WMS1120_R01Filter.productDateRange.from) : undefined,
				endDate: WMS1120_R01Filter.productDateRange?.to ? formatDate(WMS1120_R01Filter.productDateRange.to) : undefined,
				compCdCondition: WMS1120_R01Filter.productCompCdCondition || undefined,
				itemCdCondition: WMS1120_R01Filter.productItemCdCondition || undefined,
				workProcessCondition: WMS1120_R01Filter.productWorkProcessCondition || undefined,
				listLength: productBaseList.length || 0,
			};

		const response = await getWMS1120_R01ProductBase('search', params);
		const newList = response.data.data || [];
		setProductBaseList(newList);

		if (newList.length > 0) {
			// 첫 번째 행 자동 선택 (UI)
			gridRefSelectedRowHandler(
				productGridRef,
				setSelectedProductBase,
				() => true,
			);

			const firstRow = newList[0];
			if (!firstRow.productResultNo) {
				setProductLotList([]);
				setSelectedProductLot(undefined);
				setProductResultList([]);
				setSelectedProductResult(undefined);
				setBomList([]);
				setSelectedBom(undefined);
				setStockList([]);
			} else {
				// 첫 행 기준 연쇄 조회: Lot → 생산정보 → BOM → 입고이력
				const lotRes = await getWMS1120_R01ProductLotDetail(firstRow.productResultNo);
				const lotList: WMS1120_R01ProductLotDto[] = lotRes.data.data || [];
				setProductLotList(lotList);
				setSelectedProductLot(lotList.length > 0 ? { ...lotList[0], valueIndex: 1 } : undefined);

				if (lotList.length > 0) {
					const prRes = await getWMS1120_R01ProductResult(lotList[0].lotNo);
					const productResultList: WMS1120_R01ProductResultDto[] = prRes.data.data || [];
					setProductResultList(productResultList);
					setSelectedProductResult(productResultList.length > 0 ? productResultList[0] : undefined);

					if (productResultList.length > 0) {
						const bomRes = await getWMS1120_R01MaterialInput(productResultList[0].productResultNo);
						const bomListData: WMS1120_R01BomDto[] = bomRes.data.data || [];
						setBomList(bomListData);
						setSelectedBom(bomListData.length > 0 ? { ...bomListData[0], valueIndex: 1 } : undefined);

						if (bomListData.length > 0) {
							const stockRes = await getWMS1120_R01LotInbound(bomListData[0].lotNo);
							setStockList(stockRes.data.data || []);
						} else {
							setStockList([]);
						}
					} else {
						setBomList([]);
						setSelectedBom(undefined);
						setStockList([]);
					}
				} else {
					setProductResultList([]);
					setSelectedProductResult(undefined);
					setBomList([]);
					setSelectedBom(undefined);
					setStockList([]);
				}
			}

			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			setSelectedProductBase(undefined);
			setProductLotList([]);
			setSelectedProductLot(undefined);
			setProductResultList([]);
			setSelectedProductResult(undefined);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
		}
		} catch (error) {
			// console.error('생산기준 조회 오류:', error);
			showWarning('조회 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [WMS1120_R01Filter, productBaseList.length, setProductBaseList, setSelectedProductBase, setProductLotList, setSelectedProductLot, setProductResultList, setSelectedProductResult, setBomList, setSelectedBom, setStockList, setMessage, setLoadingSpinner, productGridRef, resetAllWMS1120ScrollTop]);

	// 매출 행 클릭 시 Lot 조회
	const handleSalesRowClick = useCallback(async (data: WMS1120_R01SalesDto) => {
		resetWMS1120ScrollTop(['salesLot', 'productResult', 'bom', 'stock']);
		if (!data.salesBillNo) {
			showWarning('매출전표번호가 없습니다.');
			setSalesLotList([]);
			setSelectedSalesLot(undefined);
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			return;
		}
		
		try {
			// 1. 매출기준 Lot 조회 (상단 Lot 테이블용)
			const lotResponse = await getWMS1120_R01SalesLot(data.salesBillNo);
			const lotList: WMS1120_R01SalesLotDto[] = lotResponse.data.data || [];
			setSalesLotList(lotList);

			// 매출 Lot 선택 상태 초기화
			setSelectedSalesLot(undefined);

			// 하단 테이블은 Lot 클릭 시 조회되도록 초기화
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			
		} catch (error) {
			setSalesLotList([]);
			setSelectedSalesLot(undefined);
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
		}
	}, [setSalesLotList, setSelectedSalesLot, setProductResultList, setBomList, setSelectedBom, setStockList, resetWMS1120ScrollTop]);

	// 생산 행 클릭 시 Lot 조회
	const handleProductRowClick = useCallback(async (data: WMS1120_R01ProductBaseDto) => {
		resetWMS1120ScrollTop(['productLot', 'productResult', 'bom', 'stock']);
		if (!data.productResultNo) {
			showWarning('생산실적번호가 없습니다.');
			setProductLotList([]);
			setSelectedProductLot(undefined);
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			return;
		}
		
		try {
			// 1. 생산 Lot 조회 (상단 Lot 테이블용) - productResultNo 사용
			const productLotResponse = await getWMS1120_R01ProductLotDetail(data.productResultNo);
			const lotList: WMS1120_R01ProductLotDto[] = productLotResponse.data.data || [];
			setProductLotList(lotList);

			// 생산 Lot 선택 상태 초기화
			setSelectedProductLot(undefined);

			// 하단 테이블 초기화 (LOT 생산정보 행 클릭 시 조회됨)
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			
		} catch (error) {
			setProductLotList([]);
			setSelectedProductLot(undefined);
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
		}
	}, [setBomList, setProductResultList, setProductLotList, setSelectedProductLot, setStockList, setSelectedBom, resetWMS1120ScrollTop]);

	// BOM 행 클릭 시 Lot 입고 근거 조회
	const handleBomRowClick = useCallback(async (data: WMS1120_R01BomDto) => {
		resetWMS1120ScrollTop(['stock']);
		if (!data.lotNo) {
			showWarning('Lot 번호가 없습니다.');
			setStockList([]);
			return;
		}
		
		try {
			// Lot 번호 기준으로 입고 근거 조회
			const stockResponse = await getWMS1120_R01LotInbound(data.lotNo);
			setStockList(stockResponse.data.data || []);
		} catch (error) {
			setStockList([]);
		}
	}, [setStockList, resetWMS1120ScrollTop]);

	// ========================================
	// 매출 Lot 클릭 시 생산 정보 조회
	// ========================================
	const handleSalesLotRowClick = useCallback(async (data: WMS1120_R01SalesLotDto) => {
		resetWMS1120ScrollTop(['productResult', 'bom', 'stock']);
		if (!data.lotNo) {
			setProductResultList([]);
			setBomList([]);         // BOM 리스트도 초기화
			setStockList([]);       // 입고이력도 초기화
			return;
		}
		
		try {
			// 1️⃣ Lot 번호로 생산 이력 조회
			const productResultResponse = await getWMS1120_R01ProductResult(data.lotNo);
			const productResultList = productResultResponse.data.data || [];
			setProductResultList(productResultList);
			
			// 2️⃣ 하단 테이블 초기화 (LOT 생산정보 행 클릭 시 조회됨)
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			
		} catch (error) {
			// 에러 발생 시 모든 하단 테이블 초기화
			setProductResultList([]);
			setBomList([]);
			setStockList([]);
		}
	}, [setProductResultList, setBomList, setStockList, setSelectedBom, resetWMS1120ScrollTop]);

	// 생산 Lot 클릭 시 생산 정보 조회
	const handleProductLotRowClick = useCallback(async (data: WMS1120_R01ProductLotDto) => {
		resetWMS1120ScrollTop(['productResult', 'bom', 'stock']);
		if (!data.lotNo) {
			setProductResultList([]);
			return;
		}
		
		try {
			// Lot 번호로 생산 이력 조회
			const productResultResponse = await getWMS1120_R01ProductResult(data.lotNo);
			setProductResultList(productResultResponse.data.data || []);
		} catch (error) {
			setProductResultList([]);
		}
	}, [setProductResultList, resetWMS1120ScrollTop]);

	// LOT 생산정보 행 클릭 시 자재 투입 내역 조회
	const handleProductResultRowClick = useCallback(async (data: WMS1120_R01ProductResultDto) => {
		resetWMS1120ScrollTop(['bom', 'stock']);
		if (!data.productResultNo) {
			setBomList([]);
			setStockList([]);
			return;
		}
		
		try {
			// 생산실적번호로 자재 투입 내역 조회
			const bomResponse = await getWMS1120_R01MaterialInput(data.productResultNo);
			setBomList(bomResponse.data.data || []);
			setSelectedBom(undefined);
			
			// 입고이력은 자재 행 클릭 시 조회되므로 초기화
			setStockList([]);
		} catch (error) {
			setBomList([]);
			setStockList([]);
		}
	}, [setBomList, setStockList, setSelectedBom, resetWMS1120ScrollTop]);

	// 탭별 조회
	const fetchData = useCallback(async () => {
		if (activeTab === 'sales') {
			await fetchSalesData();
		} else {
			await fetchProductData();
		}
	}, [activeTab, fetchSalesData, fetchProductData]);

	// 탭 변경 시 자동 조회 (초기 마운트 제외)
	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab]);

	return {
		fetchData,
		handleSalesRowClick,
		handleProductRowClick,
		handleSalesLotRowClick,
		handleProductLotRowClick,
		handleProductResultRowClick,
		handleBomRowClick,
	};
};

